import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { availableLanguages } from '../translations';
import AppBanner from './AppBanner';
import { supabase } from '../supabaseClient';

const languageNames = {
  en: { en: 'English', es: 'Spanish', fr: 'French', he: 'Hebrew', zh: 'Mandarin', ko: 'Korean' },
  es: { en: 'Inglés', es: 'Español', fr: 'Francés', he: 'Hebreo', zh: 'Mandarín', ko: 'Coreano' },
  fr: { en: 'Anglais', es: 'Espagnol', fr: 'Français', he: 'Hébreu', zh: 'Mandarin', ko: 'Coréen' },
  he: { en: 'אנגלית', es: 'ספרדית', fr: 'צרפתית', he: 'עברית', zh: 'מנדרינית', ko: 'קוריאנית' },
  zh: { en: '英语', es: '西班牙语', fr: '法语', he: '希伯来语', zh: '中文', ko: '韩语' },
  ko: { en: 'English', es: 'Spanish', fr: 'French', he: 'Hebrew', zh: 'Mandarin', ko: '한국어' }
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { t, currentLanguage, changeLanguage, isRTL } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  // Sync selectedLanguage with currentLanguage from context
  React.useEffect(() => {
    setSelectedLanguage(currentLanguage);
  }, [currentLanguage]);

  // Real logout handler
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      alert(t('logoutError') + ': ' + error.message);
    } else {
      window.location.href = '/login';
    }
  };

  const handleLanguageChange = async (e) => {
    const lang = e.target.value;
    setSelectedLanguage(lang);
    await changeLanguage(lang);
  };

  return (
    <>
      <AppBanner onLogout={handleLogout} />
      <div className="card" style={{ maxWidth: 600, margin: '80px auto', padding: 32, background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        <h1 style={{ textAlign: 'center' }}>{t('onboardingWelcome')}</h1>
        <div style={{ margin: '24px 0', textAlign: 'center' }}>
          <strong style={{ fontWeight: 600, marginRight: 12 }}>{t('onboardingPickLanguage')}</strong>
          <select
            id="onboarding-language-select"
            value={selectedLanguage}
            onChange={handleLanguageChange}
            style={{
              marginLeft: '0.5rem',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '1rem',
              minWidth: '150px'
            }}
          >
            {availableLanguages.map(lang => {
              const currentLanguageName = languageNames[currentLanguage]?.[lang.code] || lang.name;
              return (
                <option key={lang.code} value={lang.code}>
                  {lang.nativeName} ({currentLanguageName})
                </option>
              );
            })}
          </select>
        </div>
        <div style={{
          background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
          color: '#fff',
          borderRadius: 8,
          padding: '16px 20px',
          margin: '18px 0 28px 0',
          fontSize: '1.15rem',
          fontWeight: 500,
          boxShadow: '0 2px 8px rgba(34, 139, 34, 0.10)'
        }}>
          🎁 <strong>{t('onboardingThanks')}</strong> {t('onboardingTrial')}
        </div>
        <div style={{
          background: '#f1f8e9',
          color: '#386641',
          borderRadius: 8,
          padding: '16px 20px',
          margin: '0 0 28px 0',
          fontSize: '1.08rem',
          fontWeight: 500,
          border: '1px solid #b7e4c7',
          boxShadow: '0 1px 4px rgba(56,102,65,0.06)'
        }}>
          <strong>{t('onboardingPremiumFeaturesTitle')}</strong>
          <ul style={{ margin: '12px 0 0 18px', padding: 0, color: '#386641', fontWeight: 400, fontSize: '1.02rem' }}>
            <li>{t('onboardingPremiumFeature1')}</li>
            <li>{t('onboardingPremiumFeature2')}</li>
            <li>{t('onboardingPremiumFeature3')}</li>
            <li>{t('onboardingPremiumFeature4')}</li>
          </ul>
        </div>
        <p style={{ fontSize: '1.08rem', marginBottom: 8 }}>
          {t('onboardingGettingStarted')}
        </p>
        <ul style={{ fontSize: '1.02rem', marginBottom: 24, paddingLeft: 22, textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
          <li>{t('onboardingStep1')}</li>
          <li>{t('onboardingStep2')}</li>
          <li>{t('onboardingStep3')}</li>
          <li>{t('onboardingStep4')}</li>
          <li>{t('onboardingStep5')}</li>
        </ul>
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <button onClick={async () => { try { await supabase.auth.updateUser({ data: { has_signed_in: true } }); } catch (_) { }; navigate('/getting-started'); }} className='btn btn-primary'>
            {t('onboardingGetStarted') || 'Get Started'}
          </button>
        </div>
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <p style={{ fontSize: '1.08rem', marginBottom: 12 }}>
            Are you a teacher or student?
          </p>
          <button
            onClick={async () => {
              try {
                await supabase.auth.updateUser({ data: { has_signed_in: true } });
              } catch (_) { };
              navigate('/classrooms');
            }}
            className='btn btn-secondary'
            style={{ marginTop: 8 }}
          >
            Join a Classroom
          </button>
        </div>
      </div>
    </>
  );
};

export default OnboardingPage; 